import styled from 'styled-components';
import {
  color,
  fontFamily,
  fontSize,
  fontWeight,
  radius,
  screen,
  spacing,
} from '../../../theme';
import { Heading3, Heading4 } from '../../shared/Typography';

export const StyledRequirementsEvolution = styled.div`
  margin-top: ${spacing('xl3')};

  @media (min-width: ${screen('md')}) {
    display: flex;
    align-items: flex-start;
  }
`;

export const StyledRequirementsEvolutionTitle = styled(Heading3)`
  position: relative;
  margin-bottom: ${spacing('lg')};

  &::before {
    content: '';
    position: absolute;
    top: calc(${spacing('sm')} * -1);
    width: ${spacing('xl3')};
    height: ${spacing('xs2')};
    background-color: ${color('mint_900')};
  }

  @media (min-width: ${screen('md')}) {
    display: none;
  }
`;

export const StyledRequirementsEvolutionContainer = styled.div`
  position: relative;

  @media (min-width: ${screen('md')}) {
    margin-right: ${spacing('xl')};
    max-width: 610px;
    width: 100%;
  }

  @media (max-width: ${screen('mdMax')}) {
    svg {
      display: none;
    }
  }
`;

export const StyledRequirementsEvolutionContent = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;

  @media (max-width: ${screen('mdMax')}) {
    padding-left: ${spacing('xl3')};
    padding-right: ${spacing('xl3')};
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: calc(${spacing('xl3')} + ${spacing('lg')} / 2);
      right: calc(${spacing('xl3')} + ${spacing('lg')} / 2);
      height: ${spacing('lg')};
      background-color: ${color('sand_150')};
      clip-path: polygon(0 50%, 100% 0, 100% 100%);
    }

    &[data-milestone='2']::before {
      background-image: linear-gradient(
        to right,
        ${color('text_active')},
        ${color('text_active')} 25%,
        transparent 50%
      );
    }

    &[data-milestone='3']::before {
      background-image: linear-gradient(
        to right,
        ${color('text_active')},
        ${color('text_active')} 50%,
        transparent
      );
    }
  }

  @media (min-width: ${screen('md')}) {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }
`;

export const StyledRequirementsEvolutionClient = styled.img`
  display: block;
  position: absolute;
  max-width: 7rem;
  max-height: 7rem;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  @media (max-width: ${screen('mdMax')}) {
    display: none;
  }

  @media (min-width: ${screen('lg')}) {
    max-width: 9rem;
    max-height: 9rem;
  }
`;

export const StyledRequirementsEvolutionTrigger = styled.button`
  display: block;
  width: ${spacing('lg')};
  height: ${spacing('lg')};
  border-radius: ${radius('full')};
  background: radial-gradient(
    circle at center,
    ${color('sand_500')} 0,
    ${color('sand_500')} ${spacing('xs2')},
    ${color('white')} ${spacing('xs2')}
  );
  border: 0;
  box-shadow: 0 0 0 3px ${color('sand_150')};
  position: relative;

  @media (max-width: ${screen('mdMax')}) {
    &.is-active {
      background: radial-gradient(
        circle at center,
        ${color('active')} 0,
        ${color('active')} ${spacing('xs2')},
        ${color('white')} ${spacing('xs2')}
      );
      box-shadow: 0 0 0 3px ${color('emerald_150')};

      &::before {
        animation: none;
      }

      &::after {
        color: ${color('text_active')};
      }
    }
  }

  @media (min-width: ${screen('md')}) {
    position: absolute;
  }

  &::after {
    content: attr(aria-label);
    width: 5rem;
    position: absolute;
    top: 50%;
    left: 1.25rem;
    transform: translateY(-50%);
    font-size: ${fontSize('xs')};
    font-weight: ${fontWeight('extraBold')};
    color: ${color('text_default')};
    font-family: ${fontFamily('sans')};

    @media (max-width: ${screen('mdMax')}) {
      top: unset;
      bottom: -1.5rem;
      left: 50%;
      transform: translateX(-50%);
    }
  }

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${spacing('xl3')};
    height: ${spacing('xl3')};
    margin: -1.5rem auto auto -1.5rem;
    transform-origin: 50% 50%;
    border-radius: 50%;
    border: 1px solid ${color('sand_500')};
    opacity: 0;
    animation: pulsate 3s ease-out infinite;
  }

  &:hover {
    cursor: pointer;

    &::before {
      animation: none;
    }
  }

  &:active,
  &:focus {
    outline: 0;

    &::before {
      animation: stop-pulsate 0.3s;
    }
  }

  @keyframes pulsate {
    0% {
      transform: scale(1);
      opacity: 0.8;
    }
    45% {
      transform: scale(1.75);
      opacity: 0;
    }
  }

  @keyframes stop-pulsate {
    from {
      opacity: 0.4;
    }
    to {
      transform: scale(2);
      opacity: 0;
    }
  }

  &.is-month-one {
    @media (min-width: ${screen('md')}) {
      top: 19%;
      left: 65%;
    }
  }

  &.is-month-three {
    @media (min-width: ${screen('md')}) {
      top: 48%;
      left: 82%;
    }

    &::after {
      @media (min-width: ${screen('md')}) {
        left: 1.5rem;
      }
    }
  }

  &.is-month-six {
    @media (min-width: ${screen('md')}) {
      top: 82%;
      left: 48%;
    }

    &::after {
      @media (min-width: ${screen('md')}) {
        left: 50%;
        top: 1.75rem;
        transform: translateX(-50%);
      }
    }
  }
`;

export const StyledRequirementsEvolutionDay0 = styled.div`
  position: absolute;
  top: 7.5%;
  left: 50%;
  transform: translateX(-50%);
  background: ${color('midnight_800')};
  color: ${color('text_default_inverse')};
  border-radius: ${radius('sm')};
  font-size: ${fontSize('sm')};
  line-height: 1.33;
  font-weight: ${fontWeight('medium')};
  text-align: center;
  padding: ${spacing('sm')};

  &::before {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 0px;
    height: 0px;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid ${color('midnight_800')};
  }

  @media (max-width: ${screen('mdMax')}) {
    display: none;
  }
`;

export const StyledRequirementsMilestonesContainer = styled.div`
  padding-top: ${spacing('xl')};

  @media (min-width: ${screen('md')}) {
    max-width: 15rem;
  }

  @media (min-width: ${screen('lg')}) {
    padding-top: 15%;
    max-width: 20rem;
  }
`;

export const StyledRequirementsMilestone = styled.div`
  @media (min-width: ${screen('md')}) {
    max-height: 30em;
    overflow: auto;
  }
  ${Heading4} {
    margin-bottom: ${spacing('md')};

    @media (max-width: ${screen('mdMax')}) {
      display: none;
    }
  }

  ul {
    margin-left: ${spacing('lg')};
  }

  ul li {
    list-style-type: disc;
  }
`;

export const StyledRequirementsMilestonesArrows = styled.div`
  display: flex;
  margin-bottom: ${spacing('md')};

  @media (max-width: ${screen('mdMax')}) {
    display: none;
  }

  button {
    width: ${spacing('xl')};
    height: ${spacing('xl')};
    background: transparent;
    border: 0;
    padding: 0;
    color: ${color('midnight_800')};
    margin-right: ${spacing('xs2')};
    cursor: pointer;
    transition: 0.3s;

    &:hover {
      color: ${color('midnight_600')};
    }

    &[disabled] {
      color: ${color('midnight_150')};
      pointer-events: none;
    }

    svg {
      width: ${spacing('xl')} !important;
      height: ${spacing('xl')};
    }
  }
`;
